import type { ExtensionCommandContext } from "@earendil-works/pi-coding-agent";
import type { PiMentisConfig } from "@pi-mentis/pi-mentis-core";

import { MaskedSecretInput } from "./secret-input.js";
import {
  ProviderConfigStore,
  ProviderRegistry,
  SILICONFLOW_PROVIDER,
  providerDraft,
  resolveCredential,
  type MentisProviderDefinition,
  type ProviderConfigDraft,
  type SecretStore,
} from "./provider-settings.js";
import {
  formatSystemSettingValue,
  parseSystemSettingInput,
  systemSettingGroups,
  systemSettingOption,
  type SystemSettingField,
  type SystemSettingValue,
} from "./system-settings.js";

export interface ProviderRuntimeStatus {
  readonly ready: boolean;
  readonly providerId: string;
  readonly providerName: string;
  readonly credentialSource: "secure" | "environment" | "missing";
  readonly configured: boolean;
  readonly endpoint: string;
  readonly embeddingModel: string;
  readonly rerankEnabled: boolean;
  readonly rerankModel?: string;
}

export interface ProviderTestResult {
  readonly providerId: string;
  readonly providerName: string;
  readonly authentication: "passed" | "failed" | "missing";
  readonly embedding: "passed" | "failed" | "not-run";
  readonly rerank: "passed" | "failed" | "disabled" | "not-run";
  readonly embeddingModel: string;
  readonly rerankModel?: string;
  readonly latencyMs: number;
  readonly error?: {
    readonly category: string;
    readonly message: string;
    readonly httpStatus?: number;
  };
}

export interface ProviderReloadResult {
  readonly activated: boolean;
  readonly revision?: number;
  readonly error?: { readonly category: string; readonly message: string };
}

export interface ProviderModelCatalogResult {
  readonly providerId: string;
  readonly providerName: string;
  readonly embeddingModels: readonly string[];
  readonly rerankModels: readonly string[];
  readonly source: "remote" | "verified-fallback";
  readonly error?: { readonly category: string; readonly message: string };
}

export interface MentisProviderRuntimeControl {
  status(): Promise<ProviderRuntimeStatus>;
  reload(): Promise<ProviderReloadResult>;
  reloadSystem?(): Promise<ProviderReloadResult>;
  test(providerId?: "siliconflow" | "openrouter"): Promise<ProviderTestResult>;
  models(providerId?: "siliconflow" | "openrouter"): Promise<ProviderModelCatalogResult>;
}

function sourceLabel(source: ProviderRuntimeStatus["credentialSource"]): string {
  if (source === "secure") return "Secure settings";
  if (source === "environment") return "Environment variable";
  return "Missing";
}

function formatStatus(status: ProviderRuntimeStatus, environmentOverridden = false): string {
  return [
    "Mentis",
    "",
    `Memory             ${status.ready ? "ready" : "degraded / setup required"}`,
    `Provider           ${status.providerName}`,
    `API key            ${status.configured ? "configured" : "not configured"}`,
    `Credential source  ${sourceLabel(status.credentialSource)}`,
    ...(environmentOverridden ? ["Environment key    present, overridden"] : []),
    `Embedding          ${status.embeddingModel}`,
    `Reranker           ${status.rerankEnabled ? (status.rerankModel ?? "unknown") : "disabled"}`,
    `Provider health    ${status.ready && status.configured ? "ready" : "setup required"}`,
  ].join("\n");
}

function providerTestPassed(result: ProviderTestResult): boolean {
  return (
    result.authentication === "passed" &&
    result.embedding === "passed" &&
    (result.rerank === "passed" || result.rerank === "disabled") &&
    result.error === undefined
  );
}

export class MentisSettingsController {
  readonly #registry: ProviderRegistry;
  readonly #configStore: ProviderConfigStore;
  readonly #secretStore: SecretStore;
  readonly #runtime: MentisProviderRuntimeControl;
  readonly #environment: NodeJS.ProcessEnv;

  constructor(options: {
    readonly configStore: ProviderConfigStore;
    readonly secretStore: SecretStore;
    readonly runtime: MentisProviderRuntimeControl;
    readonly registry?: ProviderRegistry;
    readonly environment?: NodeJS.ProcessEnv;
  }) {
    this.#configStore = options.configStore;
    this.#secretStore = options.secretStore;
    this.#runtime = options.runtime;
    this.#registry = options.registry ?? new ProviderRegistry();
    this.#environment = options.environment ?? process.env;
  }

  async handle(
    rawArguments: string,
    context: ExtensionCommandContext,
  ): Promise<PiMentisConfig | undefined> {
    const action = rawArguments.trim().toLowerCase();
    if (action === "") return await this.#mainSettings(context);
    if (action === "provider") return await this.#providerMenu(context);
    if (action === "status") {
      await this.#showStatus(context);
      return undefined;
    }
    if (action === "config") return await this.#providerSettings(context);
    if (action === "system" || action === "settings") return await this.#systemSettings(context);
    if (action === "key") {
      await this.#credential(context);
      return undefined;
    }
    if (action === "doctor" || action === "test") {
      await this.#doctor(context);
      return undefined;
    }
    context.ui.notify(
      "Available commands:\n  /mentis\n  /mentis provider\n  /mentis status\n  /mentis config\n  /mentis system\n  /mentis key\n  /mentis doctor",
      "error",
    );
    return undefined;
  }

  async #mainSettings(context: ExtensionCommandContext): Promise<PiMentisConfig | undefined> {
    let latestConfig: PiMentisConfig | undefined;
    while (true) {
      const choice = await context.ui.select("Mentis Settings", [
        "Provider",
        "System Settings",
        "Status",
        "Doctor",
        "Close",
        "Reset Settings",
      ]);
      if (choice === undefined || choice === "Close") return latestConfig;
      if (choice === "Provider") {
        latestConfig = (await this.#providerMenu(context)) ?? latestConfig;
        continue;
      }
      if (choice === "System Settings") {
        latestConfig = (await this.#systemSettings(context)) ?? latestConfig;
        continue;
      }
      if (choice === "Status") {
        await this.#showStatus(context);
        continue;
      }
      if (choice === "Doctor") {
        await this.#doctor(context);
        continue;
      }
      if (choice === "Reset Settings") {
        latestConfig = (await this.#resetSettings(context)) ?? latestConfig;
      }
    }
  }

  async #resetSettings(context: ExtensionCommandContext): Promise<PiMentisConfig | undefined> {
    const confirmed = await context.ui.confirm(
      "Reset all Mentis settings?",
      "Provider models and all system settings will return to defaults. Your API Key and stored memory or knowledge data will not be deleted.",
    );
    if (!confirmed) return undefined;
    try {
      const reset = await this.#configStore.reset(context.cwd, this.#environment);
      const reload = await (this.#runtime.reloadSystem?.() ?? this.#runtime.reload());
      if (reload.activated) {
        context.ui.notify("All Mentis settings reset to defaults and activated.", "info");
        return reset.config;
      }
      const restored = await this.#configStore.restore(
        context.cwd,
        reset.previous,
        this.#environment,
      );
      const restoredRuntime = await (this.#runtime.reloadSystem?.() ?? this.#runtime.reload());
      if (!restoredRuntime.activated) {
        context.ui.notify(
          "Settings reset failed and the previous runtime could not be confirmed. Mentis is degraded.",
          "error",
        );
        return restored.config;
      }
      context.ui.notify(
        `Settings were not reset; the previous configuration was restored: ${reload.error?.message ?? "reload failed"}`,
        "warning",
      );
      return restored.config;
    } catch (error) {
      context.ui.notify(
        error instanceof Error ? error.message : "Unable to reset Mentis settings",
        "error",
      );
      return undefined;
    }
  }

  async #providerMenu(context: ExtensionCommandContext): Promise<PiMentisConfig | undefined> {
    let latestConfig: PiMentisConfig | undefined;
    while (true) {
      const config = await this.#configStore.load(context.cwd, this.#environment);
      const definitions = this.#registry.list();
      const currentDefinition =
        this.#registry.get(config.inference.provider) ?? SILICONFLOW_PROVIDER;
      const resolutions = await Promise.all(
        definitions.map(async (definition) => ({
          definition,
          credential: await resolveCredential(
            definition,
            this.#secretStore,
            this.#environment,
            false,
          ),
        })),
      );
      const labels = resolutions.map(({ definition, credential }) =>
        [
          definition.displayName,
          credential.configured ? "Configured" : "Setup required",
          ...(definition.id === config.inference.provider ? ["Current"] : []),
        ].join(" — "),
      );
      const currentLabel = `Current Provider — ${currentDefinition.displayName}`;
      const choice = await context.ui.select("Provider", [currentLabel, ...labels, "Back"]);
      if (choice === undefined || choice === "Back") return latestConfig;
      if (choice === currentLabel) {
        latestConfig = (await this.#selectCurrentProvider(context)) ?? latestConfig;
        continue;
      }
      const selected = resolutions[labels.indexOf(choice)]?.definition;
      if (selected === undefined) continue;
      latestConfig = (await this.#providerSettings(context, selected)) ?? latestConfig;
    }
  }

  async #selectCurrentProvider(
    context: ExtensionCommandContext,
  ): Promise<PiMentisConfig | undefined> {
    const current = await this.#configStore.load(context.cwd, this.#environment);
    const configured: MentisProviderDefinition[] = [];
    for (const definition of this.#registry.list()) {
      const resolution = await resolveCredential(
        definition,
        this.#secretStore,
        this.#environment,
        false,
      );
      if (resolution.configured) configured.push(definition);
    }
    if (configured.length === 0) {
      context.ui.notify(
        "Configure a Provider API Key before selecting the current Provider.",
        "warning",
      );
      return undefined;
    }
    const labels = configured.map((definition) =>
      definition.id === current.inference.provider
        ? `${definition.displayName} — Current`
        : definition.displayName,
    );
    const choice = await context.ui.select("Current Provider", [...labels, "Back"]);
    if (choice === undefined || choice === "Back") return undefined;
    const selected = configured[labels.indexOf(choice)];
    if (selected === undefined || selected.id === current.inference.provider) return undefined;
    const previousDraft = providerDraft(current);
    try {
      const saved = await this.#configStore.save(
        context.cwd,
        providerDraft(current, selected.id),
        this.#environment,
      );
      const reload = await (this.#runtime.reloadSystem?.() ?? this.#runtime.reload());
      const activationError = reload.activated
        ? await this.#providerValidationError(selected.id)
        : (reload.error?.message ?? "reload failed");
      if (activationError === undefined) {
        context.ui.notify(`Current Provider changed to ${selected.displayName}.`, "info");
        return saved.config;
      }
      await this.#configStore.save(context.cwd, previousDraft, this.#environment);
      await (this.#runtime.reloadSystem?.() ?? this.#runtime.reload());
      context.ui.notify(`Current Provider was not changed: ${activationError}`, "warning");
      return undefined;
    } catch (error) {
      context.ui.notify(
        error instanceof Error ? error.message : "Unable to change the current Provider",
        "error",
      );
      return undefined;
    }
  }

  async #showStatus(context: ExtensionCommandContext): Promise<void> {
    const status = await this.#runtime.status();
    const definition = this.#registry.get(status.providerId) ?? SILICONFLOW_PROVIDER;
    const credential = await resolveCredential(
      definition,
      this.#secretStore,
      this.#environment,
      false,
    );
    context.ui.notify(
      formatStatus(status, credential.source === "secure" && credential.environmentPresent),
      status.ready ? "info" : "warning",
    );
  }

  async #providerSettings(
    context: ExtensionCommandContext,
    selectedDefinition?: MentisProviderDefinition,
  ): Promise<PiMentisConfig | undefined> {
    let latestConfig: PiMentisConfig | undefined;
    const initialConfig = await this.#configStore.load(context.cwd, this.#environment);
    const definition =
      selectedDefinition ??
      this.#registry.get(initialConfig.inference.provider) ??
      SILICONFLOW_PROVIDER;
    const initialCredential = await resolveCredential(
      definition,
      this.#secretStore,
      this.#environment,
      false,
    );
    if (!initialCredential.configured && !(await this.#credential(context, definition))) {
      return undefined;
    }
    while (true) {
      const config = await this.#configStore.load(context.cwd, this.#environment);
      const draft = providerDraft(config, definition.id);
      const resolution = await resolveCredential(
        definition,
        this.#secretStore,
        this.#environment,
        false,
      );
      const choice = await context.ui.select(definition.displayName, [
        `API Key — ${resolution.configured ? `Configured (${sourceLabel(resolution.source)})` : "Missing"}`,
        `Embedding Model — ${draft.embeddingModel}`,
        ...(definition.capabilities.rerank
          ? [
              `Rerank Model — ${config.inference.rerank.enabled ? (draft.rerankModel ?? "Unknown") : "Disabled"}`,
            ]
          : []),
        "Doctor",
        ...(resolution.securePresent ? ["Remove Stored API Key"] : []),
        "Back",
      ]);
      if (choice === undefined || choice === "Back") return latestConfig;
      if (choice.startsWith("API Key")) {
        await this.#credential(context, definition);
        continue;
      }
      if (choice.startsWith("Embedding Model")) {
        latestConfig = (await this.#selectModel(context, "embedding", definition)) ?? latestConfig;
        continue;
      }
      if (choice.startsWith("Rerank Model")) {
        latestConfig = (await this.#selectModel(context, "rerank", definition)) ?? latestConfig;
        continue;
      }
      if (choice === "Doctor") {
        await this.#doctor(context, definition);
        continue;
      }
      if (choice === "Remove Stored API Key") {
        await this.#removeCredential(context, definition);
        return latestConfig;
      }
    }
  }

  async #credential(
    context: ExtensionCommandContext,
    selectedDefinition?: MentisProviderDefinition,
  ): Promise<boolean> {
    const runtimeStatus = await this.#runtime.status();
    const definition =
      selectedDefinition ?? this.#registry.get(runtimeStatus.providerId) ?? SILICONFLOW_PROVIDER;
    const secret = await context.ui.custom<string | undefined>(
      (tui, theme, keybindings, done) =>
        new MaskedSecretInput(tui, theme, keybindings, done, definition.displayName),
    );
    if (secret === undefined) return false;
    let previousStored: string | undefined;
    try {
      previousStored = await this.#secretStore.get(definition.credential.secretId);
      await this.#secretStore.set(definition.credential.secretId, secret);
    } catch {
      context.ui.notify("Failed to store credential securely.", "error");
      return false;
    }
    const validationError = await this.#providerValidationError(definition.id);
    if (validationError !== undefined) {
      const restored = await this.#restoreCredential(definition, previousStored);
      context.ui.notify(
        restored
          ? `${definition.displayName} API Key was not changed: ${validationError}`
          : `${definition.displayName} API Key validation failed and the previous credential could not be restored.`,
        restored ? "warning" : "error",
      );
      return false;
    }
    if (runtimeStatus.providerId !== definition.id) {
      context.ui.notify(
        `${definition.displayName} API Key saved. Select it under Current Provider when ready.`,
        "info",
      );
      return true;
    }
    const reload = await this.#runtime.reload();
    if (!reload.activated) {
      const restored = await this.#restoreCredential(definition, previousStored);
      if (restored) await this.#runtime.reload();
      context.ui.notify(
        restored
          ? `${definition.displayName} API Key was not changed: ${reload.error?.message ?? "provider activation failed"}`
          : `${definition.displayName} API Key activation failed and the previous credential could not be restored.`,
        restored ? "warning" : "error",
      );
      return false;
    }
    context.ui.notify(`${definition.displayName} API Key saved and activated.`, "info");
    return true;
  }

  async #restoreCredential(
    definition: MentisProviderDefinition,
    previousStored: string | undefined,
  ): Promise<boolean> {
    try {
      if (previousStored === undefined) {
        await this.#secretStore.delete(definition.credential.secretId);
      } else {
        await this.#secretStore.set(definition.credential.secretId, previousStored);
      }
      return true;
    } catch {
      return false;
    }
  }

  async #providerValidationError(
    providerId: MentisProviderDefinition["id"],
  ): Promise<string | undefined> {
    try {
      const result = await this.#runtime.test(providerId);
      if (providerTestPassed(result)) return undefined;
      return result.error?.message ?? "Provider health validation failed";
    } catch (error) {
      return error instanceof Error ? error.message : "Provider health validation failed";
    }
  }

  async #systemSettings(context: ExtensionCommandContext): Promise<PiMentisConfig | undefined> {
    let latestConfig: PiMentisConfig | undefined;
    while (true) {
      const config = await this.#configStore.load(context.cwd, this.#environment);
      const groups = systemSettingGroups(config);
      const choice = await context.ui.select("System Settings", [
        ...groups.map((group) => group.title),
        "Back",
      ]);
      if (choice === undefined || choice === "Back") return latestConfig;
      const group = groups.find((candidate) => candidate.title === choice);
      if (group === undefined) continue;
      while (true) {
        const current = await this.#configStore.load(context.cwd, this.#environment);
        const currentGroup = systemSettingGroups(current).find(
          (candidate) => candidate.id === group.id,
        );
        if (currentGroup === undefined) break;
        const options = [...currentGroup.fields.map(systemSettingOption), "Back"];
        const fieldChoice = await context.ui.select(currentGroup.title, options);
        if (fieldChoice === undefined || fieldChoice === "Back") break;
        const field = currentGroup.fields.find(
          (candidate) => systemSettingOption(candidate) === fieldChoice,
        );
        if (field === undefined) continue;
        latestConfig = (await this.#editSystemSetting(context, field)) ?? latestConfig;
      }
    }
  }

  async #editSystemSetting(
    context: ExtensionCommandContext,
    field: SystemSettingField,
  ): Promise<PiMentisConfig | undefined> {
    let value: SystemSettingValue | undefined;
    if (typeof field.value === "boolean") {
      const choice = await context.ui.select(field.label, [
        field.value ? "Enabled — Current" : "Enabled",
        field.value ? "Disabled" : "Disabled — Current",
        "Back",
      ]);
      if (choice === undefined || choice === "Back") return undefined;
      value = choice.startsWith("Enabled");
    } else if (field.choices !== undefined) {
      const labels = field.choices.map((choice) =>
        choice === field.value ? `${choice} — Current` : choice,
      );
      const choice = await context.ui.select(field.label, [...labels, "Back"]);
      if (choice === undefined || choice === "Back") return undefined;
      value = field.choices[labels.indexOf(choice)];
    } else {
      const input = await context.ui.input(
        `Set ${field.label}`,
        `Current: ${formatSystemSettingValue(field.value)}`,
      );
      if (input === undefined) return undefined;
      try {
        value = parseSystemSettingInput(field, input);
      } catch (error) {
        context.ui.notify(
          error instanceof Error ? error.message : "Invalid setting value",
          "error",
        );
        return undefined;
      }
    }
    if (value === undefined || Object.is(value, field.value)) return undefined;
    if (field.requiresConfirmation === true) {
      const confirmed = await context.ui.confirm(
        `Change ${field.label}?`,
        "This switches the active Mentis data location. Existing data is not moved automatically.",
      );
      if (!confirmed) return undefined;
    }
    return await this.#saveSystemSetting(context, field, value);
  }

  async #saveSystemSetting(
    context: ExtensionCommandContext,
    field: SystemSettingField,
    value: SystemSettingValue,
  ): Promise<PiMentisConfig | undefined> {
    try {
      const saved = await this.#configStore.saveSystemSetting(
        context.cwd,
        field,
        value,
        this.#environment,
      );
      const reload = await (this.#runtime.reloadSystem?.() ?? this.#runtime.reload());
      if (reload.activated) {
        context.ui.notify(`${field.label} saved and activated.`, "info");
        return saved.config;
      }
      const restored = await this.#configStore.saveSystemSetting(
        context.cwd,
        field,
        field.value,
        this.#environment,
      );
      const restoredRuntime = await (this.#runtime.reloadSystem?.() ?? this.#runtime.reload());
      if (!restoredRuntime.activated) {
        context.ui.notify(
          "System setting activation failed and the previous runtime could not be confirmed. Mentis is degraded.",
          "error",
        );
        return restored.config;
      }
      context.ui.notify(
        `Setting was not changed; the previous configuration was restored: ${reload.error?.message ?? "reload failed"}`,
        "warning",
      );
      return restored.config;
    } catch (error) {
      context.ui.notify(
        error instanceof Error ? error.message : "Unable to update Mentis system setting",
        "error",
      );
      return undefined;
    }
  }

  async #removeCredential(
    context: ExtensionCommandContext,
    definition: typeof SILICONFLOW_PROVIDER,
  ): Promise<void> {
    const resolution = await resolveCredential(
      definition,
      this.#secretStore,
      this.#environment,
      false,
    );
    const after = resolution.environmentPresent
      ? "Credential source → Environment variable"
      : "Credential status → Missing";
    const confirmed = await context.ui.confirm(
      `Remove stored ${definition.displayName} credential?`,
      `This does not modify your shell environment.\n\nAfter removal: ${after}`,
    );
    if (!confirmed) return;
    try {
      await this.#secretStore.delete(definition.credential.secretId);
      const config = await this.#configStore.load(context.cwd, this.#environment);
      if (config.inference.provider === definition.id) await this.#runtime.reload();
      context.ui.notify(`Stored credential removed. ${after}`, "info");
    } catch {
      context.ui.notify("Failed to remove stored credential securely.", "error");
    }
  }

  async #selectModel(
    context: ExtensionCommandContext,
    kind: "embedding" | "rerank",
    definition?: MentisProviderDefinition,
  ): Promise<PiMentisConfig | undefined> {
    const current = await this.#configStore.load(context.cwd, this.#environment);
    const providerId = definition?.id ?? current.inference.provider;
    const active = current.inference[providerId];
    const currentModel =
      kind === "embedding" ? active.embedding.model : current.inference.siliconflow.rerank.model;
    const catalog = await this.#runtime.models(providerId);
    if (catalog.error !== undefined) {
      context.ui.notify(`Using verified model list: ${catalog.error.message}`, "warning");
    }
    const models = kind === "embedding" ? catalog.embeddingModels : catalog.rerankModels;
    if (models.length === 0) {
      context.ui.notify(`No compatible ${kind} models are available.`, "warning");
      return undefined;
    }
    const labels = models.map((model) => (model === currentModel ? `${model} — Current` : model));
    const choice = await context.ui.select(
      kind === "embedding" ? "Select Embedding Model" : "Select Rerank Model",
      labels,
    );
    if (choice === undefined) return undefined;
    const selected = models[labels.indexOf(choice)];
    if (selected === undefined || selected === currentModel) return undefined;
    const draft: ProviderConfigDraft = {
      ...providerDraft(current, providerId),
      activate: providerId === current.inference.provider,
      ...(kind === "embedding" ? { embeddingModel: selected } : { rerankModel: selected }),
    };
    const successMessage = `${kind === "embedding" ? "Embedding" : "Rerank"} model changed to ${selected}.`;
    if (providerId === current.inference.provider) {
      return await this.#saveAndActivate(context, draft, successMessage);
    }
    try {
      const saved = await this.#configStore.save(
        context.cwd,
        { ...draft, activate: false },
        this.#environment,
      );
      context.ui.notify(successMessage, "info");
      return saved.config;
    } catch (error) {
      context.ui.notify(
        error instanceof Error ? error.message : "Unable to update provider model",
        "error",
      );
      return undefined;
    }
  }

  async #saveAndActivate(
    context: ExtensionCommandContext,
    draft: ProviderConfigDraft,
    successMessage: string,
  ): Promise<PiMentisConfig | undefined> {
    const previous = await this.#configStore.load(context.cwd, this.#environment);
    const previousDraft = providerDraft(previous);
    try {
      const saved = await this.#configStore.save(context.cwd, draft, this.#environment);
      const reload = await (this.#runtime.reloadSystem?.() ?? this.#runtime.reload());
      const providerId = draft.providerId ?? previous.inference.provider;
      const activationError = reload.activated
        ? await this.#providerValidationError(providerId)
        : (reload.error?.message ?? "reload failed");
      if (activationError === undefined) {
        context.ui.notify(successMessage, "info");
        return saved.config;
      }
      const restored = await this.#configStore.save(context.cwd, previousDraft, this.#environment);
      const restoredRuntime = await (this.#runtime.reloadSystem?.() ?? this.#runtime.reload());
      if (!restoredRuntime.activated) {
        context.ui.notify(
          "Provider activation failed and the previous runtime could not be confirmed. Mentis is degraded.",
          "error",
        );
        return restored.config;
      }
      context.ui.notify(
        `Model was not changed; the previous configuration was restored: ${activationError}`,
        "warning",
      );
      return restored.config;
    } catch (error) {
      context.ui.notify(
        error instanceof Error ? error.message : "Unable to update provider model",
        "error",
      );
      return undefined;
    }
  }

  async #doctor(
    context: ExtensionCommandContext,
    selectedDefinition?: MentisProviderDefinition,
  ): Promise<void> {
    const status = await this.#runtime.status();
    const definition =
      selectedDefinition ?? this.#registry.get(status.providerId) ?? SILICONFLOW_PROVIDER;
    context.ui.notify(`Running Doctor for ${definition.displayName}…`, "info");
    const result = await this.#runtime.test(definition.id);
    const lines = [
      "Mentis Doctor",
      "",
      result.providerName,
      "",
      `Authentication     ${result.authentication === "passed" ? "✓" : result.authentication}`,
      `Embedding          ${result.embedding === "passed" ? `✓ ${result.embeddingModel}` : result.embedding}`,
      ...(result.rerank === "disabled"
        ? []
        : [
            `Reranker           ${result.rerank === "passed" ? `✓ ${result.rerankModel ?? ""}` : result.rerank}`,
          ]),
      `Latency            ${Math.round(result.latencyMs)} ms`,
      "",
      ...(result.error === undefined ? ["Doctor checks passed."] : [result.error.message]),
    ];
    context.ui.notify(lines.join("\n"), result.error === undefined ? "info" : "warning");
  }
}
